"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState, type FormEvent } from "react";

import { demandApi, startContextualChat } from "@/lib/client-api";
import { isUsableWebSession, useAuthStore } from "@/lib/auth-store";
import { authHrefFor } from "@/lib/navigation";
import { useMounted } from "@/lib/use-mounted";
import type { DemandPost } from "@/lib/types";

/**
 * "I can supply" — the structured chat-first offer form. Records the offer on
 * the demand post, then opens the conversation with the buyer.
 */
export function SupplyOfferPanel({ post }: { post: DemandPost }) {
  const router = useRouter();
  const mounted = useMounted();
  const user = useAuthStore((state) => state.user);
  const accessToken = useAuthStore((state) => state.accessToken);
  const demoMode = useAuthStore((state) => state.demoMode);
  const hasSession = mounted ? isUsableWebSession({ user, accessToken, demoMode }) : false;

  const [open, setOpen] = useState(false);
  const [availableKg, setAvailableKg] = useState("");
  const [offeredPrice, setOfferedPrice] = useState("");
  const [deliveryArea, setDeliveryArea] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const isOwner = Boolean(user && post.buyerUserId && user.id === post.buyerUserId);
  const isClosed = post.status ? post.status !== "open" : false;

  if (!mounted) return null;

  if (isOwner) {
    return (
      <div style={{ marginTop: 16 }}>
        <Link href="/activity?tab=needs" className="btn btn-primary" style={{ width: "100%" }}>
          Review responses
        </Link>
      </div>
    );
  }

  if (isClosed) {
    return (
      <div className="notice notice-ok" style={{ marginTop: 16 }}>
        This buyer need is {post.status}. Browse other <Link href="/buyer-demand">open needs</Link>.
      </div>
    );
  }

  if (!hasSession) {
    return (
      <div style={{ marginTop: 16 }}>
        <Link
          href={authHrefFor(`/buyer-demand/${post.id}`, `Supply ${post.title}`)}
          className="btn btn-primary"
          style={{ width: "100%" }}
        >
          I can supply
        </Link>
      </div>
    );
  }

  if (done) {
    return (
      <div className="notice notice-ok" style={{ marginTop: 16 }}>
        Offer sent. Track it under <Link href="/activity?tab=needs">My activity</Link> and continue in{" "}
        <Link href="/messages">Messages</Link>.
      </div>
    );
  }

  if (!open) {
    return (
      <div style={{ marginTop: 16 }}>
        <button type="button" className="btn btn-primary" style={{ width: "100%" }} onClick={() => setOpen(true)}>
          I can supply
        </button>
      </div>
    );
  }

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError("");

    if (demoMode) {
      setDone(true);
      return;
    }

    // Delivery area rides in the message until the response row has its own field.
    const messageParts = [
      deliveryArea.trim() ? `Delivery area: ${deliveryArea.trim()}` : null,
      note.trim() || null,
    ].filter(Boolean);

    try {
      await demandApi.respond(post.id, {
        message: messageParts.length > 0 ? messageParts.join("\n") : undefined,
        offeredPrice: offeredPrice ? Number(offeredPrice) : undefined,
        availableQuantity: availableKg ? Number(availableKg) : undefined,
      });

      if (post.buyerUserId) {
        try {
          const { href } = await startContextualChat({
            targetUserId: post.buyerUserId,
            contextType: "demand",
            contextTitle: post.title,
            contextId: post.id,
          });
          router.push(href);
          return;
        } catch {
          // The offer already landed; chat can start from Messages later.
        }
      }
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ display: "grid", gap: 10, marginTop: 16 }}>
      <label className="field">
        <span>Quantity you can supply ({post.unit})</span>
        <input
          className="input"
          type="number"
          min={0}
          step="any"
          value={availableKg}
          onChange={(event) => setAvailableKg(event.target.value)}
          required
        />
      </label>
      <label className="field">
        <span>Your price per {post.unit} ({post.currency})</span>
        <input
          className="input"
          type="number"
          min={0}
          step="any"
          value={offeredPrice}
          onChange={(event) => setOfferedPrice(event.target.value)}
          required
        />
      </label>
      <label className="field">
        <span>Delivery area</span>
        <input
          className="input"
          value={deliveryArea}
          onChange={(event) => setDeliveryArea(event.target.value)}
          placeholder="Where you can deliver from/to"
          maxLength={200}
        />
      </label>
      <label className="field">
        <span>Note to the buyer (optional)</span>
        <textarea
          className="textarea"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Grade, availability dates, or anything else the buyer should know."
          maxLength={1500}
          rows={3}
        />
      </label>

      {error ? <div className="notice notice-error">{error}</div> : null}

      <div className="row-actions">
        <button className="btn btn-primary" type="submit" disabled={submitting}>
          {submitting ? "Sending offer..." : "Send offer and chat"}
        </button>
        <button className="btn btn-ghost" type="button" onClick={() => setOpen(false)} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function formatApiError(err: unknown): string {
  if (err && typeof err === "object" && "response" in err) {
    const response = (err as { response?: { data?: { message?: unknown } } }).response;
    const message = response?.data?.message;
    if (Array.isArray(message)) return message.join(", ");
    if (typeof message === "string") return message;
  }
  return err instanceof Error ? err.message : "Could not send your offer.";
}
